import type { Province } from '../../types/province';
import { formatNumber, formatNetworthPerAcre } from '../../utils/format';
import { resolveTradeColor } from '../throne/throneFormatting';
import { GlanceCard } from './GlanceCard';
import { MetricRow } from './MetricRow';

interface SummaryCardProps {
  province: Province | null;
}

/**
 * Standing + Resources GlanceCards — the full field set `ResourceBar` shows (Land, Networth,
 * NW/Acre, Honour, Gold, Food, Runes, Trade). Two separate components (not one combined fragment)
 * because `DesktopThroneShell`'s card feed interleaves Armies Away/Power between them — they
 * aren't always adjacent.
 *
 * SF-138 follow-up: mobile no longer uses these — `AppShellLayout`'s sticky header now mounts
 * `<ResourceBar>` directly (see its own doc comment), so `MobileThroneShell` and the `RealmHub`/
 * `WarHub`/`KingdomHub`/`WorldHub` mobile-only hub bodies dropped their `StandingCard`/
 * `ResourcesCard` pair as a now-redundant second source of the same numbers. `DesktopThroneShell`
 * is this component's only remaining consumer — desktop `AppShellLayout` routes still have no
 * `ResourceBar` of their own.
 */
export function StandingCard({ province }: SummaryCardProps) {
  if (!province) {
    return (
      <GlanceCard title="Standing" icon="🏰">
        <div className="text-sm text-game-400">Loading…</div>
      </GlanceCard>
    );
  }

  const { metadata, resources, territory } = province;
  const acres = territory.totals.acres;

  return (
    <GlanceCard title="Standing" icon="🏰">
      <MetricRow label="Land" value={formatNumber(acres)} unit="acres" weight="primary" />
      <MetricRow label="Networth" value={formatNumber(metadata.networth)} unit="nw" weight="secondary" />
      <MetricRow label="NW/Acre" value={formatNetworthPerAcre(metadata.networth, acres)} unit="nw" weight="tertiary" />
      <MetricRow label="Honour" value={formatNumber(resources.honour)} unit="" weight="tertiary" />
    </GlanceCard>
  );
}

export function ResourcesCard({ province }: SummaryCardProps) {
  if (!province) {
    return (
      <GlanceCard title="Resources" icon="💰">
        <div className="text-sm text-game-400">Loading…</div>
      </GlanceCard>
    );
  }

  const { resources } = province;
  const tradePrefix = resources.tradeBalance > 0 ? '+' : '';
  const tradeColor = resolveTradeColor(resources.tradeBalance);

  return (
    <GlanceCard title="Resources" icon="💰">
      <MetricRow label="Gold" value={formatNumber(resources.gold)} unit="gc" weight="primary" />
      <MetricRow label="Food" value={formatNumber(resources.food)} unit="bushels" weight="secondary" />
      <MetricRow label="Runes" value={formatNumber(resources.runes)} unit="runes" weight="tertiary" />
      <MetricRow
        label="Trade"
        value={`${tradePrefix}${formatNumber(resources.tradeBalance)}`}
        unit="gc"
        weight="tertiary"
        valueColor={tradeColor}
      />
    </GlanceCard>
  );
}
